'use client';

type OperationalFormLike = {
  form_key?: string | null;
  title?: string | null;
  category?: string | null;
  is_required?: boolean | null;
  required_for_job?: boolean | null;
  required_for_appointment?: boolean | null;
  compliance_meta?: Record<string, unknown> | string | null;
  tags?: string[] | string | null;
};

type OperationalFormSubmissionLike = {
  form_key?: string | null;
  overall_status?: string | null;
  status?: string | null;
};

function parseComplianceMeta(value: OperationalFormLike['compliance_meta']): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed: unknown = JSON.parse(value);
      return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  return value;
}

function normalizeTags(tags: OperationalFormLike['tags']): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.filter((tag): tag is string => typeof tag === 'string');
  if (typeof tags === 'string') {
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) {
        return parsed.filter((tag): tag is string => typeof tag === 'string');
      }
    } catch {
      return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    }
  }
  return [];
}

export function getOperationalFormDisplayLabel(form: Pick<OperationalFormLike, 'title' | 'form_key'>): string {
  return form.title?.trim() || form.form_key?.trim() || 'Untitled form';
}

export type OperationalFormRequirementScope = 'any' | 'job' | 'appointment';

function hasScopedRequirement(
  form: OperationalFormLike,
  scope: OperationalFormRequirementScope,
): boolean {
  if (scope === 'job') {
    return Boolean(form.required_for_job);
  }

  if (scope === 'appointment') {
    return Boolean(form.required_for_appointment);
  }

  return Boolean(form.required_for_job || form.required_for_appointment);
}

export function isOperationalFormRequired(
  form: OperationalFormLike,
  scope: OperationalFormRequirementScope = 'any',
): boolean {
  const complianceMeta = parseComplianceMeta(form.compliance_meta);
  const tags = normalizeTags(form.tags);

  // If is_compulsory is explicitly false, the admin opted out of the
  // "required for job completion" gate — respect that even for compliance category.
  const explicitlyNotCompulsory = complianceMeta.is_compulsory === false;

  const builtInRequired = Boolean(
    form.is_required
    || complianceMeta.require_all_pass
    || complianceMeta.is_compulsory
    || (form.category === 'compliance' && !explicitlyNotCompulsory)
    || tags.includes('required')
  );

  if (scope === 'appointment') {
    return hasScopedRequirement(form, scope);
  }

  return builtInRequired || hasScopedRequirement(form, scope);
}

export function isOperationalFormSubmissionComplete(submission: OperationalFormSubmissionLike): boolean {
  const normalizedStatus = (submission.overall_status || submission.status || '').trim().toLowerCase();
  // NOTE: 'conditional' is intentionally excluded — a partially-failed form
  // does NOT satisfy the completion gate. The technician must resolve all
  // conditional/fail sections before the form counts as complete.
  return ['pass', 'submitted', 'approved'].includes(normalizedStatus);
}

/**
 * Preview gate for report/PDF rendering — intentionally broader than the
 * completion gate above. A failed or conditional submission still produces a
 * meaningful report (e.g. a failed property inspection is the defect list used
 * for deposit deductions), so any finalized submission is printable.
 */
export function isOperationalFormSubmissionPreviewable(submission: OperationalFormSubmissionLike): boolean {
  const normalizedStatus = (submission.overall_status || submission.status || '').trim().toLowerCase();
  return ['pass', 'fail', 'conditional', 'submitted', 'approved'].includes(normalizedStatus);
}

export function getIncompleteRequiredOperationalForms<TForm extends OperationalFormLike, TSubmission extends OperationalFormSubmissionLike>(
  forms: TForm[],
  submissions: TSubmission[],
  options?: {
    scope?: OperationalFormRequirementScope;
  },
): TForm[] {
  const scope = options?.scope || 'any';
  const completedFormKeys = new Set(
    submissions
      .filter((submission) => isOperationalFormSubmissionComplete(submission))
      .map((submission) => submission.form_key?.trim())
      .filter((formKey): formKey is string => Boolean(formKey))
  );

  return forms.filter((form) => {
    if (!isOperationalFormRequired(form, scope)) {
      return false;
    }

    const formKey = form.form_key?.trim();
    if (!formKey) {
      return true;
    }

    return !completedFormKeys.has(formKey);
  });
}

/**
 * Check if a form is within its editable window after an appointment ends.
 *
 * Returns true if:
 * - The form has `compliance_meta.editable_window_days` set (> 0)
 * - The job is not closed (no `jobClosedAt`)
 * - The appointment was completed within the window (or no completion time
 *   is available, in which case we allow it)
 */
export function isFormWithinEditableWindow(
  form: OperationalFormLike,
  appointmentCompletedAt?: string | null,
  jobClosedAt?: string | null,
): boolean {
  const complianceMeta = parseComplianceMeta(form.compliance_meta);
  const windowDays = complianceMeta.editable_window_days;
  if (!windowDays || typeof windowDays !== 'number' || windowDays <= 0) {
    return false;
  }
  // Job closed → window is over
  if (jobClosedAt) return false;
  // No appointment completion time → can't determine window, allow it
  if (!appointmentCompletedAt) return true;
  // Check if within the window
  const completedMs = new Date(appointmentCompletedAt).getTime();
  if (!Number.isFinite(completedMs)) return true;
  const elapsedMs = Date.now() - completedMs;
  const windowMs = windowDays * 24 * 60 * 60 * 1000;
  return elapsedMs <= windowMs;
}